"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.mergeClusterArray = exports.findEdgesByFeature = exports.getMainClassAndPercentage = exports.canLinkClusterByMainClass = exports.hasLinkageByMainClass = exports.createClusterFromFeature = exports.computeClustersByAIClass = exports.computeClusterByMainClass = exports.genGraphFromClusters = exports.convertFeaturesToClusters = exports.computeSubGraph = void 0;
const turf_1 = require("@turf/turf");
const lodash_1 = __importDefault(require("lodash"));
const graph_1 = require("./graph");
/**
 * @constant
 * The min feature area be eligible for cluster
 */
const MIN_ELIGIBLE_AREA = 10000;
/**
 * @constant
 * The min percentage for feature with classes mixed
 */
const MIN_ELIGIBLE_PERCENTAGE = 0.5;
/**
 * Removes edges from a graph where the features are not similar enough.
 * If two features are nearby and have the same AI class, they will be
 * in the same cluster.
 * But if two features are nearby and have different AI classes, they will
 * only be in the same cluster if the area of the smaller feature (and nearby
 * ones of the same class) is less than 1.0 ha.
 *
 * @param graph Based on the edges present in this graph
 * @returns A graph of edges linking the features
 */
function computeSubGraph(graph) {
    const init_clusters = convertFeaturesToClusters(graph);
    const ai_clusters = computeClustersByAIClass(init_clusters);
    const clusters = computeClusterByMainClass(ai_clusters);
    return genGraphFromClusters(clusters);
}
exports.computeSubGraph = computeSubGraph;
/**
 * Create separated cluster per feature.
 *
 * @param graph Based on the edges present in this graph
 * @returns List of graph cluster with one feature per cluster
 */
function convertFeaturesToClusters(graph) {
    // Bottom-up
    return [...new Set(graph.edges.map((edge) => [edge.featureA, edge.featureB]).flat())].map((feature) => createClusterFromFeature(feature, graph));
}
exports.convertFeaturesToClusters = convertFeaturesToClusters;
/**
 * Convert clusters into graph
 *
 * @param clusters Clusters with edges
 * @returns A graph of edges linking the features
 */
function genGraphFromClusters(clusters) {
    return {
        edges: [...new Set(clusters.map((c) => c.in_edges).flat())],
    };
}
exports.genGraphFromClusters = genGraphFromClusters;
/**
 * Compute graph clusters by area and main classes.
 *
 * Hierarchical clustering: (https://en.wikipedia.org/wiki/Hierarchical_clustering)
 * Compute AI clusters first, and then compute clusters based on main class and the area.
 * - Start from the smallest AI cluster
 * - The main class of an AI cluster with the total area over 1.0 ha could not be changed
 * - The main class percentage of a final cluster (mixed with multiple classes) should not less than 50%
 *
 * @param clusters Clusters by AI classes
 * @returns List of graph cluster
 */
function computeClusterByMainClass(clusters) {
    // AI clusters ordered by area descending, shallow copied feature for compare
    const sorted_clusters = clusters.sort((a, b) => b.total_area - a.total_area);
    while (hasLinkageByMainClass(sorted_clusters)) {
        for (let idx1 = 0; idx1 < sorted_clusters.length; idx1 += 1) {
            let cluster_1 = sorted_clusters[idx1];
            if (cluster_1 !== undefined) {
                for (let idx2 = idx1; idx2 < sorted_clusters.length; idx2 += 1) {
                    const cluster_2 = sorted_clusters[idx2];
                    if (cluster_2 !== undefined &&
                        cluster_1 !== cluster_2 &&
                        canLinkClusterByMainClass(cluster_1, cluster_2)) {
                        cluster_1 = mergeClusterArray([cluster_1, cluster_2]);
                        // Set idx2 as undefined, but keep the array element space
                        delete sorted_clusters[idx2];
                    }
                }
                sorted_clusters[idx1] = cluster_1;
            }
        }
    }
    return sorted_clusters.filter((c) => c !== undefined);
}
exports.computeClusterByMainClass = computeClusterByMainClass;
/**
 * Compute clusters based on AI classes.
 *
 * In the result clusters:
 * - The features in one cluster will the same AI class
 * - One cluster may have only one feature
 * - Two linked clusters should have different (main) classes
 *
 * @param clusters Initial clusters with one feature per cluster
 * @returns Clusters splitted by AI classes
 */
function computeClustersByAIClass(clusters) {
    const pairs = (0, graph_1.arrayCross)(clusters);
    const to_unit = pairs.filter((pair) => {
        const [cluster_1, cluster_2] = pair;
        return (cluster_1.out_edges.filter((edge) => {
            var _a, _b;
            return cluster_2.out_edges.includes(edge) &&
                ((_a = edge.featureA.properties) === null || _a === void 0 ? void 0 : _a.class) === ((_b = edge.featureB.properties) === null || _b === void 0 ? void 0 : _b.class);
        }).length > 0);
    });
    const clustersToCluster = (0, graph_1.pairsToClusters)(to_unit);
    const outClusters = [...clustersToCluster].map((clusters) => mergeClusterArray([...clusters], true));
    // Cluster with only one feature should be added to output
    const left_features = lodash_1.default.difference([...new Set(clusters.map((c) => c.in_features).flat())], [...new Set(outClusters.map((c) => c.in_features).flat())]);
    const left_clusters = clusters.filter((c) => left_features.includes(c.in_features[0]));
    const mixedClusters = [...outClusters, ...left_clusters];
    return mixedClusters;
}
exports.computeClustersByAIClass = computeClustersByAIClass;
/**
 * Generate a cluster from one feature
 *
 * @param feature AI feature
 * @param graph AI feature graph
 * @returns Cluster with one feature only
 */
function createClusterFromFeature(feature, graph) {
    var _a, _b;
    const f_area = (0, turf_1.area)(feature);
    return {
        total_area: f_area,
        area_class: [
            {
                area: f_area,
                ai_class: ((_a = feature.properties) === null || _a === void 0 ? void 0 : _a.class) || '',
            },
        ],
        main_class: (_b = feature.properties) === null || _b === void 0 ? void 0 : _b.class,
        main_class_percentage: 1.0,
        main_class_locked: f_area >= MIN_ELIGIBLE_AREA,
        in_edges: [],
        in_features: [feature],
        out_edges: findEdgesByFeature(graph, feature),
    };
}
exports.createClusterFromFeature = createClusterFromFeature;
/**
 * Check if there are cluster linked (with the main AI class)
 *
 * @param clusters Cluster list to search linkage for
 * @returns If some clusters linked
 */
function hasLinkageByMainClass(clusters) {
    for (let idx1 = 0; idx1 < clusters.length; idx1 += 1) {
        for (let idx2 = idx1 + 1; idx2 < clusters.length; idx2 += 1) {
            const cluster_1 = clusters[idx1];
            const cluster_2 = clusters[idx2];
            if (cluster_1 !== undefined &&
                cluster_2 !== undefined &&
                cluster_1 !== cluster_2 &&
                canLinkClusterByMainClass(cluster_1, cluster_2)) {
                return true;
            }
        }
    }
    return false;
}
exports.hasLinkageByMainClass = hasLinkageByMainClass;
/**
 * Check if two clusters can be combined while keep the same main class
 *
 * Lock of main class:
 * - The total area of one AI cluster (with the same class) is larger than 1.0 ha
 *
 * @param cluster_1 Original cluster to be combined to
 * @param cluster_2 New cluster to be combined
 * @returns If some clusters linked
 */
function canLinkClusterByMainClass(cluster_1, cluster_2) {
    const linkedEdges = cluster_1.out_edges.filter((edge) => cluster_2.out_edges.includes(edge));
    if (linkedEdges.length > 0) {
        const combined = mergeClusterArray([cluster_1, cluster_2]);
        // ┌────────────┬─────────────┬─────────┬────┐
        // │     C1     │     C2      │ Combined│    │
        // │ Lock  Class│ Lock  Class │Class    │    │
        // ├────────────┼─────────────┼─────────┼────┤
        // │ T     A    │ T     A     │A        │OK  │
        // │ T     A    │ T     B     │*        │NG  │
        // │ T     A    │ F     *     │A        │OK  │
        // │ T     A    │ F     *     │B        │NG  │
        // │ F     *    │ T     A     │A        │OK  │
        // │ F     *    │ T     A     │B        │NG  │
        // └────────────┴─────────────┴─────────┴────┘
        if ((cluster_1.main_class_locked &&
            cluster_2.main_class_locked &&
            cluster_1.main_class === cluster_2.main_class) ||
            (cluster_1.main_class_locked &&
                !cluster_2.main_class_locked &&
                cluster_1.main_class === combined.main_class) ||
            (!cluster_1.main_class_locked &&
                cluster_2.main_class_locked &&
                cluster_2.main_class === combined.main_class) ||
            (!cluster_1.main_class_locked && !cluster_2.main_class_locked)) {
            if (combined.main_class_percentage >= MIN_ELIGIBLE_PERCENTAGE) {
                return true;
            }
        }
    }
    return false;
}
exports.canLinkClusterByMainClass = canLinkClusterByMainClass;
/**
 * Calculate main class and percentage from original AreaClass data
 *
 * @param area_class Array of AreaClass data
 * @returns Main class and percentage of main class
 */
function getMainClassAndPercentage(area_class) {
    const grouped = area_class.reduce((group, item) => {
        if (!group[item.ai_class]) {
            // eslint-disable-next-line no-param-reassign
            group[item.ai_class] = 0.0;
        }
        // eslint-disable-next-line no-param-reassign
        group[item.ai_class] += item.area;
        return group;
    }, {});
    const max_class = Object.keys(grouped).reduce((a, b) => (grouped[a] > grouped[b] ? a : b));
    const total_area = area_class.map((a) => a.area).reduce((a, b) => a + b, 0);
    return [max_class, grouped[max_class] / total_area];
}
exports.getMainClassAndPercentage = getMainClassAndPercentage;
/**
 * Find edges which connect to the given feature.
 *
 * @param graph The graph to search for edges in
 * @param feature The feature to find edges
 * @returns Edges which connect to the given feature
 */
function findEdgesByFeature(graph, feature) {
    return graph.edges.filter((edge) => edge.featureA === feature || edge.featureB === feature);
}
exports.findEdgesByFeature = findEdgesByFeature;
/**
 * Combine clusters which don't have in_edges into a new cluster
 *
 * @param clusters Cluster list to be combined
 * @param is_for_ai_cluster Flag to create AI cluster (tier-1)
 * @returns The combined cluster from a array
 */
function mergeClusterArray(clusters, is_for_ai_cluster = false) {
    const total_area = clusters.map((c) => c.total_area).reduce((a, b) => a + b, 0);
    const area_class = [...new Set(clusters.map((cluster) => cluster.area_class).flat())];
    const [main_class, main_percentage] = getMainClassAndPercentage(area_class);
    const all_features = [...new Set(clusters.map((c) => c.in_features).flat())];
    const clusters_edges = [...new Set(clusters.map((c) => c.out_edges).flat())];
    const linkedEdges = clusters_edges.filter((edge) => all_features.includes(edge.featureA) && all_features.includes(edge.featureB));
    const inside_edges = [...new Set([...clusters.map((c) => c.in_edges).flat(), ...linkedEdges])];
    return {
        total_area: total_area,
        area_class: area_class,
        main_class: main_class,
        main_class_percentage: main_percentage,
        main_class_locked: is_for_ai_cluster
            ? total_area >= MIN_ELIGIBLE_AREA
            : clusters.map((c) => c.main_class_locked).includes(true),
        in_features: all_features,
        in_edges: inside_edges,
        out_edges: [...new Set(lodash_1.default.difference(clusters_edges, inside_edges))],
    };
}
exports.mergeClusterArray = mergeClusterArray;
